import { useEffect, useState } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import { Input } from 'antd';
import useApi from '../../../utils/AxiosWrapper';
import { useUser } from '../../../utils/UserContext';

import { AddressComponents } from '../../../utils/AddressUtils';
import SignupFooter from '../SignupFooter';
import { Entity, PartnerSignupStep } from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { patchPartner } from './PartnerSignupApis';
import FlexibleAddressInput from '../customers/FlexibleAddressInput';
import { formatEinInput } from './formatEinInput';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';

const { TextArea } = Input;

interface PartnerBusinessInfoProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (newStep: string) => void;
}

export type PartnerBusinessInfoForm = {
  businessName: string;
  businessPhone: string;
  businessAddress: string;
  websiteUrl: string;
  mcNumber: string;
  einNumber: string;
  descriptionOfServices: string;
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
};

function PartnerBusinessInfo({
  currStep,
  selectedRole,
  setStep,
}: PartnerBusinessInfoProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<PartnerBusinessInfoForm>({
    defaultValues: {
      businessName: '',
      businessPhone: '',
      businessAddress: '',
      websiteUrl: '',
      mcNumber: '',
      einNumber: '',
    },
  });
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const [addressFields, setAddressFields] = useState<AddressComponents>({});
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);

  const onSubmit: SubmitHandler<PartnerBusinessInfoForm> = (data) => {
    safeSessionSetItem('partnerBusinessInfo', JSON.stringify(data));
    patchPartner(
      data,
      userId ?? '',
      callProtectedApi,
      handleSetLoading,
      setStep,
      PartnerSignupStep.PartnerPersonalInfo
    );
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  useEffect(() => {
    for (const key in addressFields) {
      if (addressFields.hasOwnProperty(key)) {
        setValue(
          key as keyof PartnerBusinessInfoForm,
          addressFields[key as keyof AddressComponents]
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFields]);

  useEffect(() => {
    // Retrieve form data from session storage
    const savedData = safeSessionGetItem('partnerBusinessInfo');
    if (savedData) {
      const parsedData: PartnerBusinessInfoForm = JSON.parse(savedData);
      // Set form data using setValue
      for (const key in parsedData) {
        if (parsedData.hasOwnProperty(key)) {
          setValue(
            key as keyof PartnerBusinessInfoForm,
            parsedData[key as keyof PartnerBusinessInfoForm]
          );
        }
      }
      if (parsedData?.address_city?.length) {
        setAddressSelectedFromDropdown(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <>
            <style>
              {`
                   .pac-container {
                     box-shadow: none !important;
                     border: 1px solid #ddd; /* Optional: Add a light border */
                   }
                 `}
            </style>
            <div
              className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px]`}
            >
              {!loading ? (
                <>
                  <div
                    className={`flex w-[60%] flex-col items-center justify-center space-y-3`}
                  >
                    <h2 className="text-center text-[30px] font-bold">
                      Business Information
                    </h2>
                    <p className="text-[16px] text-graysecondary">
                      Provide your business details
                    </p>
                  </div>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[60%] space-y-4 pb-[10%]`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessName"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Name
                        </label>
                        <Controller
                          name="businessName"
                          control={control}
                          rules={{ required: 'Business name is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="businessName"
                              placeholder="XYZ Logistics"
                              status={errors.businessName ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.businessName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="businessPhone"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Phone
                        </label>
                        <Controller
                          name="businessPhone"
                          control={control}
                          rules={{
                            required: 'Business phone number is required',
                            pattern: {
                              value: /^\+1[0-9]{10}$/,
                              message:
                                'Phone number must be in the format +1XXXXXXXXXX',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="businessPhone"
                              placeholder="8004360832"
                              status={errors.businessPhone ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                              onChange={(e) => {
                                // Automatically format the input to start with +1
                                let value = e.target.value;
                                if (!value.startsWith('+1')) {
                                  value = `+1${value.replace(/^\+1|[^0-9]/g, '')}`;
                                }
                                field.onChange(value);
                              }}
                            />
                          )}
                        />
                        {errors.businessPhone && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessPhone.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-start gap-x-[16px]">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessAddress"
                          className="block text-[16px] text-graysecondary"
                        >
                          First Location Business Address
                        </label>
                        <FlexibleAddressInput
                          searchFieldName="businessAddress"
                          streetNumberFieldName="address_st_number"
                          streetNameFieldName="address_st_name"
                          unitFieldName="address_unit"
                          cityFieldName="address_city"
                          stateFieldName="address_state"
                          zipFieldName="address_zip"
                          register={register}
                          control={control}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                          initialMode="autocomplete"
                          searchPlaceholder="123 Main St, City, State"
                          onAddressComponentsChange={setAddressFields}
                          addressSelectedFromDropdown={
                            addressSelectedFromDropdown
                          }
                          setAddressSelectedFromDropdown={
                            setAddressSelectedFromDropdown
                          }
                          cookieKey="businessAddressPartnerMode"
                        />
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="websiteUrl"
                          className="block text-[16px] text-graysecondary"
                        >
                          Website URL
                        </label>
                        <Controller
                          name="websiteUrl"
                          control={control}
                          rules={{ required: 'Website URL is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="websiteUrl"
                              placeholder="https://xyzlogistics.com"
                              status={errors.websiteUrl ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.websiteUrl && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.websiteUrl.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="mcNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          MC Number
                        </label>
                        <Controller
                          name="mcNumber"
                          control={control}
                          rules={{ required: 'MC Number is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="mcNumber"
                              placeholder="123456"
                              status={errors.mcNumber ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.mcNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.mcNumber.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="einNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          EIN Number
                        </label>
                        <Controller
                          name="einNumber"
                          control={control}
                          rules={{
                            required: 'EIN Number is required',
                            pattern: {
                              value: /^[0-9]{2}-[0-9]{7}$/,
                              message: 'EIN must be in the format XX-XXXXXXX',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="einNumber"
                              placeholder="12-3456789"
                              status={errors.einNumber ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                              onChange={(e) => {
                                field.onChange(formatEinInput(e.target.value));
                              }}
                            />
                          )}
                        />
                        {errors.einNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.einNumber.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="w-full">
                      <label
                        htmlFor="descriptionOfServices"
                        className="block text-[16px] text-graysecondary"
                      >
                        Description of Services
                      </label>
                      <Controller
                        name="descriptionOfServices"
                        control={control}
                        rules={{
                          required: 'Description of services is required',
                        }}
                        render={({ field }) => (
                          <TextArea
                            {...field}
                            id="descriptionOfServices"
                            placeholder="Describe the services you offer"
                            status={errors.descriptionOfServices ? 'error' : ''}
                            className="mt-1"
                            style={{ borderRadius: 0, minHeight: '150px' }}
                            rows={6}
                          />
                        )}
                      />
                      {errors.descriptionOfServices && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.descriptionOfServices.message}
                        </p>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                    />
                  </form>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              )}
            </div>
          </>
        );
      case 'tablet':
        return (
          <>
            <style>
              {`
                 .pac-container {
                   box-shadow: none !important;
                   border: 1px solid #ddd; /* Optional: Add a light border */
                 }
               `}
            </style>
            <div
              className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px]`}
            >
              {!loading ? (
                <>
                  <div
                    className={`flex w-full flex-col items-center justify-center space-y-3 px-4`}
                  >
                    <h2 className="text-center text-[30px] font-bold">
                      Business Information
                    </h2>
                    <p className="text-[16px] text-graysecondary">
                      Provide your business details
                    </p>
                  </div>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-full space-y-4 px-4 pb-[10%]`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessName"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Name
                        </label>
                        <Controller
                          name="businessName"
                          control={control}
                          rules={{ required: 'Business name is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="businessName"
                              placeholder="XYZ Logistics"
                              status={errors.businessName ? 'error' : ''}
                              className="mt-1"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.businessName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="businessPhone"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Phone
                        </label>
                        <Controller
                          name="businessPhone"
                          control={control}
                          rules={{
                            required: 'Business phone number is required',
                            pattern: {
                              value: /^\+1[0-9]{10}$/,
                              message:
                                'Phone number must be in the format +1XXXXXXXXXX',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="businessPhone"
                              placeholder="8004360832"
                              status={errors.businessPhone ? 'error' : ''}
                              className="mt-1"
                              style={{ borderRadius: 0 }}
                              onChange={(e) => {
                                // Automatically format the input to start with +1
                                let value = e.target.value;
                                if (!value.startsWith('+1')) {
                                  value = `+1${value.replace(/^\+1|[^0-9]/g, '')}`;
                                }
                                field.onChange(value);
                              }}
                            />
                          )}
                        />
                        {errors.businessPhone && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessPhone.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-start gap-x-[16px]">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessAddress"
                          className="block text-[16px] text-graysecondary"
                        >
                          First Location Business Address
                        </label>
                        <FlexibleAddressInput
                          searchFieldName="businessAddress"
                          streetNumberFieldName="address_st_number"
                          streetNameFieldName="address_st_name"
                          unitFieldName="address_unit"
                          cityFieldName="address_city"
                          stateFieldName="address_state"
                          zipFieldName="address_zip"
                          register={register}
                          control={control}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                          initialMode="autocomplete"
                          searchPlaceholder="123 Main St, City, State"
                          onAddressComponentsChange={setAddressFields}
                          addressSelectedFromDropdown={
                            addressSelectedFromDropdown
                          }
                          setAddressSelectedFromDropdown={
                            setAddressSelectedFromDropdown
                          }
                          cookieKey="businessAddressPartnerMode"
                        />
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="websiteUrl"
                          className="block text-[16px] text-graysecondary"
                        >
                          Website URL
                        </label>
                        <Controller
                          name="websiteUrl"
                          control={control}
                          rules={{ required: 'Website URL is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="websiteUrl"
                              placeholder="https://xyzlogistics.com"
                              status={errors.websiteUrl ? 'error' : ''}
                              className="mt-1"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.websiteUrl && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.websiteUrl.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="mcNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          MC Number
                        </label>
                        <Controller
                          name="mcNumber"
                          control={control}
                          rules={{ required: 'MC Number is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="mcNumber"
                              placeholder="123456"
                              status={errors.mcNumber ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.mcNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.mcNumber.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="einNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          EIN Number
                        </label>
                        <Controller
                          name="einNumber"
                          control={control}
                          rules={{
                            required: 'EIN Number is required',
                            pattern: {
                              value: /^[0-9]{2}-[0-9]{7}$/,
                              message: 'EIN must be in the format XX-XXXXXXX',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="einNumber"
                              placeholder="12-3456789"
                              status={errors.einNumber ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                              onChange={(e) => {
                                field.onChange(formatEinInput(e.target.value));
                              }}
                            />
                          )}
                        />
                        {errors.einNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.einNumber.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="w-full">
                      <label
                        htmlFor="descriptionOfServices"
                        className="block text-[16px] text-graysecondary"
                      >
                        Description of Services
                      </label>
                      <Controller
                        name="descriptionOfServices"
                        control={control}
                        rules={{
                          required: 'Description of services is required',
                        }}
                        render={({ field }) => (
                          <TextArea
                            {...field}
                            id="descriptionOfServices"
                            placeholder="Describe the services you offer"
                            status={errors.descriptionOfServices ? 'error' : ''}
                            className="mt-1"
                            style={{ borderRadius: 0, minHeight: '150px' }}
                            rows={6}
                          />
                        )}
                      />
                      {errors.descriptionOfServices && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.descriptionOfServices.message}
                        </p>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                    />
                  </form>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              )}
            </div>
          </>
        );
      case 'mobile':
        return (
          <>
            <style>
              {`
                 .pac-container {
                   box-shadow: none !important;
                   border: 1px solid #ddd; /* Optional: Add a light border */
                 }
               `}
            </style>
            <div
              className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px]`}
            >
              {!loading ? (
                <>
                  <div
                    className={`flex w-full flex-col items-center justify-center space-y-3`}
                  >
                    <h2 className="text-center text-[30px] font-bold">
                      Business Information
                    </h2>
                    <p className="text-[16px] text-graysecondary">
                      Provide your business details
                    </p>
                  </div>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[95%] space-y-4 pb-[10%]`}
                  >
                    <div>
                      <label
                        htmlFor="businessName"
                        className="block text-[16px] text-graysecondary"
                      >
                        Business Name
                      </label>
                      <Controller
                        name="businessName"
                        control={control}
                        rules={{ required: 'Business name is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="businessName"
                            placeholder="XYZ Logistics"
                            status={errors.businessName ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.businessName && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.businessName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="businessPhone"
                        className="block text-[16px] text-graysecondary"
                      >
                        Business Phone
                      </label>
                      <Controller
                        name="businessPhone"
                        control={control}
                        rules={{
                          required: 'Business phone number is required',
                          pattern: {
                            value: /^\+1[0-9]{10}$/,
                            message:
                              'Phone number must be in the format +1XXXXXXXXXX',
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="businessPhone"
                            placeholder="8004360832"
                            status={errors.businessPhone ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                            onChange={(e) => {
                              // Automatically format the input to start with +1
                              let value = e.target.value;
                              if (!value.startsWith('+1')) {
                                value = `+1${value.replace(/^\+1|[^0-9]/g, '')}`;
                              }
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                      {errors.businessPhone && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.businessPhone.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="businessAddress"
                        className="block text-[16px] text-graysecondary"
                      >
                        First Location Business Address
                      </label>
                      <FlexibleAddressInput
                        searchFieldName="businessAddress"
                        streetNumberFieldName="address_st_number"
                        streetNameFieldName="address_st_name"
                        unitFieldName="address_unit"
                        cityFieldName="address_city"
                        stateFieldName="address_state"
                        zipFieldName="address_zip"
                        register={register}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        initialMode="autocomplete"
                        searchPlaceholder="123 Main St, City, State"
                        onAddressComponentsChange={setAddressFields}
                        addressSelectedFromDropdown={
                          addressSelectedFromDropdown
                        }
                        setAddressSelectedFromDropdown={
                          setAddressSelectedFromDropdown
                        }
                        cookieKey="businessAddressPartnerMode"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="websiteUrl"
                        className="block text-[16px] text-graysecondary"
                      >
                        Website URL
                      </label>
                      <Controller
                        name="websiteUrl"
                        control={control}
                        rules={{ required: 'Website URL is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="websiteUrl"
                            placeholder="https://xyzlogistics.com"
                            status={errors.websiteUrl ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.websiteUrl && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.websiteUrl.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="mcNumber"
                        className="block text-[16px] text-graysecondary"
                      >
                        MC Number
                      </label>
                      <Controller
                        name="mcNumber"
                        control={control}
                        rules={{ required: 'MC Number is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="mcNumber"
                            placeholder="123456"
                            status={errors.mcNumber ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.mcNumber && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.mcNumber.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="einNumber"
                        className="block text-[16px] text-graysecondary"
                      >
                        EIN Number
                      </label>
                      <Controller
                        name="einNumber"
                        control={control}
                        rules={{
                          required: 'EIN Number is required',
                          pattern: {
                            value: /^[0-9]{2}-[0-9]{7}$/,
                            message: 'EIN must be in the format XX-XXXXXXX',
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="einNumber"
                            placeholder="12-3456789"
                            status={errors.einNumber ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                            onChange={(e) => {
                              field.onChange(formatEinInput(e.target.value));
                            }}
                          />
                        )}
                      />
                      {errors.einNumber && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.einNumber.message}
                        </p>
                      )}
                    </div>

                    <div className="w-full">
                      <label
                        htmlFor="descriptionOfServices"
                        className="block text-[16px] text-graysecondary"
                      >
                        Description of Services
                      </label>
                      <Controller
                        name="descriptionOfServices"
                        control={control}
                        rules={{
                          required: 'Description of services is required',
                        }}
                        render={({ field }) => (
                          <TextArea
                            {...field}
                            id="descriptionOfServices"
                            placeholder="Describe the services you offer"
                            status={errors.descriptionOfServices ? 'error' : ''}
                            className="mt-1"
                            style={{ borderRadius: 0, minHeight: '150px' }}
                            rows={6}
                          />
                        )}
                      />
                      {errors.descriptionOfServices && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.descriptionOfServices.message}
                        </p>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                    />
                  </form>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              )}
            </div>
          </>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PartnerBusinessInfo;
